import { CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Descriptions, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Tag, Typography } from "antd";
import { useState } from "react";
import { integrationService } from "../services/integration.service";

const { Title, Text } = Typography;

export function IntegrationsPage() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [apiKeyResult, setApiKeyResult] = useState<string | null>(null);
	const [form] = Form.useForm();

	const { data, isLoading } = useQuery({
		queryKey: ["integrations"],
		queryFn: () => integrationService.list(),
	});

	const saveMutation = useMutation({
		mutationFn: (v: Record<string, unknown>) => editingId ? integrationService.update(editingId, v) : integrationService.create(v),
		onSuccess: (res) => {
			if (!editingId && res?.data?.api_key) setApiKeyResult(res.data.api_key);
			message.success(editingId ? "Yangilandi" : "Yaratildi");
			closeDrawer();
			queryClient.invalidateQueries({ queryKey: ["integrations"] });
		},
		onError: () => message.error("Xatolik"),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: number) => integrationService.remove(id),
		onSuccess: () => { message.success("O'chirildi"); queryClient.invalidateQueries({ queryKey: ["integrations"] }); },
	});

	const openCreate = () => { setEditingId(null); form.resetFields(); setDrawerOpen(true); };
	const openEdit = (item: Record<string, unknown>) => { setEditingId(item.id as number); form.setFieldsValue(item); setDrawerOpen(true); };
	const closeDrawer = () => { setDrawerOpen(false); setEditingId(null); form.resetFields(); };

	const integrations = data?.data?.integrations || [];

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<Title level={3} className="!mb-0">Integration sozlamalari</Title>
				<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Qo'shish</Button>
			</div>

			<Space direction="vertical" className="w-full" size={16}>
				{isLoading && <Card loading />}
				{integrations.map((item: Record<string, unknown>) => (
					<Card key={item.id as number}
						title={<Space><Tag color={item.type === "nvr" ? "blue" : "purple"}>{(item.type as string).toUpperCase()}</Tag>{item.name as string}</Space>}
						extra={
							<Space>
								<Button icon={<EditOutlined />} size="small" onClick={() => openEdit(item)} />
								<Popconfirm title="O'chirish?" onConfirm={() => deleteMutation.mutate(item.id as number)}>
									<Button danger icon={<DeleteOutlined />} size="small" />
								</Popconfirm>
							</Space>
						}>
						<Descriptions column={2} size="small">
							<Descriptions.Item label="Base URL">{item.base_url as string}</Descriptions.Item>
							<Descriptions.Item label="Interval">{item.interval as number} soniya</Descriptions.Item>
							<Descriptions.Item label="Faol">{(item.is_active as number) ? <Tag color="green">Ha</Tag> : <Tag color="red">Yo'q</Tag>}</Descriptions.Item>
							<Descriptions.Item label="State endpoint">{(item.state_endpoint as string) || "—"}</Descriptions.Item>
							<Descriptions.Item label="Snapshot endpoint">{(item.snapshot_endpoint as string) || "—"}</Descriptions.Item>
						</Descriptions>
					</Card>
				))}
				{!isLoading && integrations.length === 0 && <Text type="secondary">Integration sozlamalari yo'q</Text>}
			</Space>

			<Drawer title={editingId ? "Integration tahrirlash" : "Yangi integration"} open={drawerOpen} onClose={closeDrawer} width={520}
				extra={<Space><Button onClick={closeDrawer}>Bekor qilish</Button><Button type="primary" onClick={() => form.submit()} loading={saveMutation.isPending}>Saqlash</Button></Space>}>
				<Form form={form} layout="vertical" onFinish={(v) => saveMutation.mutate(v)}>
					<Form.Item name="name" label="Nomi" rules={[{ required: true }]}><Input placeholder="NVR Server" /></Form.Item>
					<Form.Item name="type" label="Turi" rules={[{ required: true }]}>
						<Select options={[{ value: "nvr", label: "NVR Server" }, { value: "ai", label: "AI Server" }]} />
					</Form.Item>
					<Form.Item name="base_url" label="Base URL" rules={[{ required: true }]}><Input placeholder="http://192.168.3.95:5001" /></Form.Item>
					<Form.Item name="username" label="Username"><Input /></Form.Item>
					<Form.Item name="password" label="Password"><Input.Password /></Form.Item>
					<Form.Item name="state_endpoint" label="State endpoint"><Input placeholder="/api/v1/device/status" /></Form.Item>
					<Form.Item name="snapshot_endpoint" label="Snapshot endpoint"><Input placeholder="/api/v1/tasks/submit" /></Form.Item>
					<Form.Item name="face_detect_endpoint" label="Face detect endpoint"><Input /></Form.Item>
					<Form.Item name="people_count_endpoint" label="People count endpoint"><Input /></Form.Item>
					<Form.Item name="interval" label="Interval (soniya)" initialValue={60}><InputNumber className="w-full" /></Form.Item>
				</Form>
			</Drawer>

			<Modal title="API Key yaratildi!" open={!!apiKeyResult} onOk={() => setApiKeyResult(null)} onCancel={() => setApiKeyResult(null)}
				cancelButtonProps={{ style: { display: "none" } }}>
				<div className="bg-gray-100 p-3 rounded font-mono text-sm break-all mb-2">{apiKeyResult}</div>
				<Button icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(apiKeyResult || ""); message.success("Nusxalandi"); }}>Nusxalash</Button>
				<div className="mt-2 text-red-500 text-sm">Bu kalit faqat bir marta ko'rsatiladi. Saqlab qo'ying!</div>
			</Modal>
		</div>
	);
}
